import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { ChatThread } from '@component/DriverChat/ChatThread/ChatThread';
import { DriverChatSeed } from '@component/DriverChat/types';

type DriverChatDrawerProps = {
  seed: DriverChatSeed;
  onClose: () => void;
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const DriverChatDrawer: FC<DriverChatDrawerProps> = ({
  seed,
  onClose,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [isEntered, setIsEntered] = useState(false);
  const { conversationId } = seed;
  const titleId = `dc-drawer-title-${conversationId}`;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const frame = requestAnimationFrame(() => {
      setIsEntered(true);
      drawerRef.current
        ?.querySelector<HTMLTextAreaElement>('textarea')
        ?.focus({ preventScroll: true });
    });
    return () => {
      cancelAnimationFrame(frame);
      opener?.focus?.({ preventScroll: true });
    };
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !drawerRef.current) return;
    const focusable = Array.from(
      drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (conversationId == null) return null;

  return (
    <div className={`dc-drawer-root ${isEntered ? 'is-entered' : ''}`}>
      <div className="dc-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={drawerRef}
        className="dc-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        data-test-id="driver-chat-drawer"
      >
        <ChatThread
          key={conversationId}
          conversationId={conversationId}
          seed={seed}
          mode="drawer"
          titleId={titleId}
          onClose={onClose}
          dataTestId="driver-chat-drawer"
        />
      </div>
    </div>
  );
};
